import backoff from "backoff";
import { v4 as uuidv4 } from 'uuid';

export interface WssServiceProps {
  failAfter: number
}
class WssService {

  socket: WebSocket

  onMessage?: (e: any) => void
  onOpen?: () => void
  onClose?: () => void
  onError?: (err?: any) => void
  onFailConnection?: () => void

  constructor() {
    this.setReconnect({
      failAfter: 10
    })
  }

  url: string = null;

  isInitialized = false;

  requestsHistory = {};

  exponentialBackoff = backoff.exponential({
    initialDelay: 100,
    maxDelay: 10000
  });

  setReconnect = (props: WssServiceProps) => {

    const failAfter = props.failAfter ? props.failAfter : 10;

    this.exponentialBackoff.failAfter(failAfter);

    this.exponentialBackoff.on('ready', (number: number, delay: number) => {
      this.init({
        url: this.url
      });
    });

    this.exponentialBackoff.on('fail', () => {
      if (this.onFailConnection) {
        this.onFailConnection();
      }
    });

  };

  init = props => {
    if (this.isInitialized) {
      return;
    }

    if (window.cwarsConnection) {
      window.cwarsConnection.onclose = function () { };
      window.cwarsConnection.close();
      delete window.cwarsConnection;
    }

    if (props.url) {
      this.url = props.url;
    }

    if (!this.onMessage) {
      this.onMessage = props.onMessage;
    }
    if (!this.onOpen) {
      this.onOpen = props.onOpen;
    }
    if (!this.onClose) {
      this.onClose = props.onClose;
    }
    if (!this.onError) {
      this.onError = props.onError;
    }
    if (!this.onFailConnection) {
      this.onFailConnection = props.onFailConnection;
    }


    if (this.url) {
      this.socket = new WebSocket(this.url);
      this.socket.onopen = this._onOpen;
      this.socket.onclose = this._onClose;
      this.socket.onmessage = this._onMessage;
      this.socket.onerror = this._onError;

      this.isInitialized = true;

      if (typeof window !== 'undefined') {
        window.cwarsConnection = this.socket;
      }
    }
  };

  _onOpen = e => {
    if (this.onOpen) {
      this.onOpen();
    }
  };
  _onClose = e => {
    if (this.onClose) {
      this.onClose();
    }
    this.isInitialized = false;
    this.exponentialBackoff.backoff();
  };
  _onMessage = e => {
    const data = JSON.parse(e.data);

    if (data.actionType === 'SERVER_ERROR') {
      if (this.onError) {
        this.onError({
          err: data,
          request: this.requestsHistory[data.requestId]
        });
      }

    } else {
      if (this.onMessage) {
        this.onMessage(e);
      }
    }
  };

  _onError = err => {
    if (Array.isArray(err)) {
      err.forEach(error => {
        console.error(error);
      });
    } else {
      console.error(err);
    }
  };

  _sendMessage = msg => {
    if (msg && msg.actionType && msg.payload && this.socket && this.socket.readyState === 1) {
      const reqId = uuidv4();
      this.requestsHistory[reqId] = msg;
      this.socket.send(JSON.stringify({ ...msg, requestId: reqId }));
    }

  };

  getInitData = () => {
    this.getSettings();
    this.getCommonInfo();
    this.getStagesInfo();
  };

  getStagesInfo = () => {
    this._sendMessage({
      actionType: 'CWARS_GET_STAGES_INFO',
      payload: {}
    });
  };

  getSettings = () => {
    this._sendMessage({
      actionType: 'CWARS_GET_SETTINGS',
      payload: {}
    });
  };

  getCommonInfo = () => {
    this._sendMessage({
      actionType: 'CWARS_GET_COMMON_INFO',
      payload: {}
    });
  };

  getLastRoundResults = clanId => {
    if (clanId) {
      this._sendMessage({
        actionType: 'CWARS_GET_LAST_ROUND_RESULTS',
        payload: {
          clan_id: clanId
        }
      });
    }
  };

  toggleAttemtsCount = isAttemptsCount => {
    this._sendMessage({
      actionType: 'CWARS_SET_ACCOUNT_SETTINGS',
      payload: {
        isAttemptsCount
      }
    });

  };

  changeJoinRoundAutomatically = joinRoundAutomatically => {
    this._sendMessage({
      actionType: 'CWARS_SET_CLAN_SETTINGS',
      payload: {
        joinRoundAutomatically
      }
    });
  };

  changeIsParticipating = (isParticipating: boolean) => {
    this._sendMessage({
      actionType: 'CWARS_SET_CLAN_SETTINGS',
      payload: {
        isParticipating
      }
    });
  };

  getRatings = (clanId, league) => {
    this._sendMessage({
      actionType: 'CWARS_GET_RATINGS',
      payload: clanId ? { clanId: clanId } : { league: league }
    });
  };

  getRatingLeaders = (clanId, league) => {
    this._sendMessage({
      actionType: 'CWARS_GET_RATINGS',
      payload: {}
    });
  };

  ratingsSearch = (query, offset, limit) => {
    this._sendMessage({
      actionType: 'CWARS_RATINGS_SEARCH',
      payload: {
        query: query,
        limit: limit,
        offset: offset
      }
    });
  };

  ratingsAutocomplete = query => {
    this._sendMessage({
      actionType: 'CWARS_RATINGS_SEARCH_AUTOCOMPLETE',
      payload: {
        query: query
      }
    });
  };

  getRoundsResultsHistory = (limit = 10, offset = 0) => {
    this._sendMessage({
      actionType: 'CWARS_GET_ROUNDS_RESULTS_HISTORY',
      payload: { limit, offset }
    });
  };


}


export default new WssService();